import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Button from '../components/ui/Button';
import { Calendar, Clock, Users, BookOpen, Download, CheckCircle, ArrowRight } from 'lucide-react';

/**
 * Success page shown after joining a class or submitting availability
 */
const SuccessPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state
  const { mode, classData, classType, availability } = location.state || {};
  
  // Redirect if accessing directly without state
  useEffect(() => {
    if (!mode) {
      navigate('/classes');
    }
  }, [mode, navigate]);

  // Generate calendar event data
  const generateCalendarData = () => {
    if (mode !== 'join' || !classData) return null;

    const { day, time, type } = classData;
    
    // Calculate next occurrence of the day
    const getNextDate = (dayName) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = new Date();
      const todayDay = today.getDay();
      const targetDay = days.indexOf(dayName);
      
      let daysUntilTarget = targetDay - todayDay;
      if (daysUntilTarget <= 0) {
        daysUntilTarget += 7; // Next week
      }
      
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntilTarget);
      return targetDate;
    };

    // Parse time and create start/end dates
    const parseTime = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      return { hours, minutes };
    };

    const classDate = getNextDate(day);
    const { hours, minutes } = parseTime(time);
    
    const startDate = new Date(classDate);
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setHours(hours + 1, minutes, 0, 0); // Assume 1 hour duration

    // Format dates for calendar
    const formatDateForCalendar = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = `${type.charAt(0).toUpperCase() + type.slice(1)} Language Class`;
    const description = `Your ${type} language class. Looking forward to seeing you there!`;
    const location = 'Online'; // You can make this dynamic

    return {
      title,
      description,
      location,
      startDate: formatDateForCalendar(startDate),
      endDate: formatDateForCalendar(endDate),
      startDateReadable: startDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      timeReadable: time
    };
  };

  const calendarData = generateCalendarData();

  // Generate calendar links
  const generateCalendarLinks = () => {
    if (!calendarData) return {};

    const { title, description, location, startDate, endDate } = calendarData;

    // Google Calendar
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;

    // Outlook
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${startDate}&enddt=${endDate}&body=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;

    // ICS file data
    const icsData = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Your Company//Your App//EN
BEGIN:VEVENT
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${title}
DESCRIPTION:${description}
LOCATION:${location}
END:VEVENT
END:VCALENDAR`;

    return { googleUrl, outlookUrl, icsData };
  };

  const { googleUrl, outlookUrl, icsData } = generateCalendarLinks();

  // Handle leaving the class
  const handleLeaveClass = async () => {
    if (!user || !classData) return;

    const confirmed = window.confirm(
      `Are you sure you want to leave the ${classData.type} class on ${classData.day} at ${classData.time}? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // Call API to leave class
      const response = await api.classes.leave(classData.$id, user.$id);
      
      if (response.success) {
        // Show success message and redirect to classes page
        alert('You have successfully left the class.');
        navigate('/classes', { 
          state: { 
            classType: classData.type,
            message: 'You have left the class successfully.' 
          } 
        });
      } else {
        throw new Error(response.message || 'Failed to leave class');
      }
    } catch (err) {
      console.error('Error leaving class:', err);
      alert('Failed to leave class. Please try again or contact support.');
    }
  };

  // Download ICS file
  const downloadICS = () => {
    if (!icsData) return;
    
    const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${calendarData.title.replace(/\s+/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSuccessMessage = () => {
    if (mode === 'join') {
      return {
        title: 'You\'re in!',
        subtitle: `You've successfully joined the ${classData.classTypeName || classData.classTypeCategory || 'class'} class`,
        icon: <CheckCircle size={48} className="text-green-400" />
      };
    } else {
      return {
        title: 'Availability Submitted!',
        subtitle: `We'll notify you when a ${classType} class forms at your preferred times`,
        icon: <CheckCircle size={48} className="text-green-400" />
      };
    }
  };

  const { title, subtitle, icon } = getSuccessMessage();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Join a Group Class
            </span>
          </h1>
          
          <div className="flex items-center">
            <span className="text-gray-300 text-sm hidden sm:inline">
              Hello, {user?.name}
            </span>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success animation and message */}
          <div className="text-center mb-8">
            {/* Chinese character decoration */}
            <div className="inline-block text-6xl font-light text-green-400 opacity-20 mb-2">
              {mode === 'join' ? '成功' : '已提交'}
            </div>
            
            <div className="mb-6 flex justify-center">
              <div className="relative">
                {icon}
                <div className="absolute inset-0 animate-ping">
                  <CheckCircle size={48} className="text-green-400 opacity-20" />
                </div>
              </div>
            </div>
            
            <h2 className="text-3xl font-bold mb-2">{title}</h2>
            <div className="w-16 h-1 bg-green-500 mx-auto mt-2"></div>
          </div>

          {/* Class details for join mode */}
          {mode === 'join' && classData && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
              <div className="bg-gradient-to-r from-green-800 to-green-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <Calendar size={20} className="mr-2" />
                  Your Class Details
                </h3>
              </div>
              
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-8 text-green-400">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Date</p>
                        <p className="font-medium">{calendarData?.startDateReadable}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-8 text-green-400">
                        <Clock size={20} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Time</p>
                        <p className="font-medium">{classData.time}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-8 text-green-400">
                        <BookOpen size={20} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Class Type</p>
                        <p className="font-medium">{classData.type.charAt(0).toUpperCase() + classData.type.slice(1)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-8 text-green-400">
                        <Users size={20} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Class Size</p>
                        <p className="font-medium">{classData.totalSpots - classData.spotsLeft + 1} of {classData.totalSpots} students</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calendar integration for join mode */}
          {mode === 'join' && calendarData && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
              <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-6 py-4 border-b border-gray-700">
                <h3 className="font-semibold text-lg flex items-center">
                  <Download size={20} className="mr-2" />
                  Add it to Your Calendar
                </h3>
              </div>
              
              <div className="p-6">
                <p className="text-gray-300 mb-6">
                  Make sure nothing gets in the way of your learning.
                </p>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    onClick={() => window.open(googleUrl, '_blank')}
                    className="border-green-500 text-green-400 hover:bg-green-500 hover:bg-opacity-10"
                  >
                    Google Calendar
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => window.open(outlookUrl, '_blank')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:bg-opacity-10"
                  >
                    Outlook
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={downloadICS}
                    className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10"
                  >
                    Download .ics
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Next steps */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg">What Happens Next?</h3>
            </div>
            
            <div className="p-6">
              {mode === 'join' ? (
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium">Confirmation Email</h4>
                      <p className="text-gray-400 text-sm">You'll receive a confirmation email with class details and joining instructions.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium">Class Reminder</h4>
                      <p className="text-gray-400 text-sm">We'll send you a reminder 24 hours before your class starts.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium">Join Your Class</h4>
                      <p className="text-gray-400 text-sm">We can't wait to see you!</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium">We'll Find Matches</h4>
                      <p className="text-gray-400 text-sm">We'll look for other students with similar availability to form a class.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium">Class Formation</h4>
                      <p className="text-gray-400 text-sm">When we have enough students, we'll create a new class at your preferred time.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4 mt-1">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium">You'll Be Notified</h4>
                      <p className="text-gray-400 text-sm">We'll email you when a class is ready so you can confirm your spot.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              onClick={() => navigate('/classes')}
              className="bg-purple-600 hover:bg-purple-700 flex items-center justify-center"
            >
              Browse More Classes
              <ArrowRight size={16} className="ml-2" />
            </Button>
            
            {mode === 'join' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate('/availability', { state: { classType: classData.type } })}
                  className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10"
                >
                  Set Availability for More Classes
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleLeaveClass}
                  className="border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10"
                >
                  Leave This Class
                </Button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SuccessPage;